import { useCallback, useRef, useState } from 'react';
import { isValidUrlProtocol } from '../../../SharedCommon/url';
import i18n from '../../../i18n';

interface IUseAudioPlayerReturn {
  playingAudioId: number | string | null;
  playAudio: (
    msg: { id: number | string; audio: { url: string; duration: number } },
    onError: (msg: string) => void
  ) => void;
  stopAudio: (clearPlayingId?: boolean) => void;
}

export const useAudioPlayer = (): IUseAudioPlayerReturn => {
  const [playingAudioId, setPlayingAudioId] = useState<number | string | null>(null);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const audioEventHandlersRef = useRef<{ handleEnded: () => void; handleError: (e: Event) => void } | null>(null);

  const stopAudio = useCallback((clearPlayingId = true) => {
    if (!audioPlayerRef.current) {
      return;
    }

    const previousAudio = audioPlayerRef.current;
    const previousHandlers = audioEventHandlersRef.current;

    if (previousHandlers) {
      previousAudio.removeEventListener('ended', previousHandlers.handleEnded);
      previousAudio.removeEventListener('error', previousHandlers.handleError);
      audioEventHandlersRef.current = null;
    }
    try {
      previousAudio.pause();
    } catch {
      // Ignore pause errors
    }
    previousAudio.src = '';
    previousAudio.load();
    audioPlayerRef.current = null;
    if (clearPlayingId) {
      setPlayingAudioId(null);
    }
  }, []);

  const playAudio = useCallback(
    (msg: { id: number | string; audio: { url: string; duration: number } }, onError: (msg: string) => void) => {
      if (!msg.audio.url || typeof msg.audio.url !== 'string') {
        onError(i18n.t('Chatroom.urlInvalid'));
        return;
      }

      // Validate URL - support data: URLs and regular URLs
      if (msg.audio.url.startsWith('data:')) {
        // data: URLs are valid, skip URL validation
      } else {
        try {
          if (!isValidUrlProtocol(msg.audio.url)) {
            onError(i18n.t('Chatroom.urlInvalid'));
            return;
          }
          new URL(msg.audio.url);
        } catch {
          onError(i18n.t('Chatroom.urlInvalid'));
          return;
        }
      }

      const audioUrl = msg.audio.url;

      const duration = msg.audio.duration;
      if (!Number.isFinite(duration) || duration <= 0 || duration > 600000) {
        onError(i18n.t('Chatroom.audioTooLong'));
        return;
      }

      if (playingAudioId === msg.id && audioPlayerRef.current) {
        stopAudio(true);
        return;
      }

      stopAudio(false);
      setPlayingAudioId(msg.id);

      try {
        const audio = new Audio(audioUrl);
        audioPlayerRef.current = audio;

        const cleanupAudioListeners = (targetAudio: HTMLAudioElement) => {
          if (audioEventHandlersRef.current) {
            const { handleEnded, handleError } = audioEventHandlersRef.current;
            targetAudio.removeEventListener('ended', handleEnded);
            targetAudio.removeEventListener('error', handleError);
          }
          audioEventHandlersRef.current = null;
          if (audioPlayerRef.current === targetAudio) {
            audioPlayerRef.current = null;
          }
        };

        const handleEnded = () => {
          setPlayingAudioId(null);
          cleanupAudioListeners(audio);
        };

        const handleError = (e: Event) => {
          setPlayingAudioId(null);
          const error = e.target as HTMLAudioElement;
          let errorKey = 'Chatroom.audioPlayFailed';

          if (error && error.error) {
            const mediaError = error.error;
            if (mediaError.code != null) {
              switch (mediaError.code) {
                case MediaError.MEDIA_ERR_ABORTED:
                  errorKey = 'Chatroom.audioPlayAborted';
                  break;
                case MediaError.MEDIA_ERR_NETWORK:
                  errorKey = 'Chatroom.audioPlayNetworkError';
                  break;
                case MediaError.MEDIA_ERR_DECODE:
                  errorKey = 'Chatroom.audioPlayDecodeError';
                  break;
                case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
                  errorKey = 'Chatroom.audioPlayFormatNotSupported';
                  break;
                default:
                  errorKey = 'Chatroom.audioPlayFailed';
              }
            }
          }

          onError(i18n.t(errorKey));
          cleanupAudioListeners(audio);
        };

        audioEventHandlersRef.current = { handleEnded, handleError };

        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('error', handleError);

        audio.play().catch((error) => {
          setPlayingAudioId(null);
          cleanupAudioListeners(audio);

          if (error.name === 'AbortError') {
            return;
          }

          let errorKey = 'Chatroom.audioPlayFailed';
          if (error.name === 'NotAllowedError') {
            errorKey = 'Chatroom.audioPlayNotAllowed';
          } else if (error.name === 'NotSupportedError') {
            errorKey = 'Chatroom.audioPlayNotSupported';
          } else if (error.name === 'NetworkError') {
            errorKey = 'Chatroom.audioPlayNetworkError';
          }

          onError(i18n.t(errorKey));
        });
      } catch {
        setPlayingAudioId(null);
        onError(i18n.t('Chatroom.audioPlayerCreateError'));
        audioPlayerRef.current = null;
        audioEventHandlersRef.current = null;
      }
    },
    [playingAudioId, stopAudio]
  );

  return {
    playingAudioId,
    playAudio,
    stopAudio
  };
};
